import React, { useCallback, useContext, useState } from 'react'
import Avatar, {
  IProps as IUserProps,
  ugcUserLink,
} from 'Components/author/avatar'
import ToolTip from 'Components/new/tool-tip'
import { Image } from 'Components/view-image'
import classNames from 'classnames'
import formatTime from 'lib/format-time'
import { GlobalStoreContext } from 'Components/store'
import useToggleFollow from 'Components/hooks/use-toggle-follow'
import formatNumber from '@arsenal/arsenal/modules/formatNumber'

export interface IMedalProps {
  name?: string
  desc?: string
  icon?: string
  time?: number
}

export const Medal: React.FC<IMedalProps> = ({ name, desc, time, icon }) => {
  return (
    <div className="tw-w-160 tw-p-12 tw-text-center tw-text-common-black">
      <Image
        width={72}
        height={72}
        className="tw-w-72 tw-h-72 tw-object-cover"
        src={icon || ''}
        autoPixelRatio
      />
      {name ? (
        <p className="tw-mt-8 line-1 tw-leading-20 tw-font-medium">{name}</p>
      ) : null}
      {desc ? (
        <p className="tw-mt-2 tw-leading-14 tw-text-12">“{desc}”</p>
      ) : null}
      {time ? (
        <p className="tw-mt-6 line-1 tw-leading-18 tw-text-12 tw-text-video-shallow-gray">
          {formatTime(time * 1000)}点亮
        </p>
      ) : null}
    </div>
  )
}

interface IProps {
  avatar?: IUserProps['avatar'] // 用户头像 url
  userId?: IUserProps['userId'] // 用户id
  username?: IUserProps['username'] // 用户名
  userVerified?: IUserProps['userVerified'] // 用户认证
  verifyType?: IUserProps['verifyType'] // 用户认证类型
  medals?: IMedalProps[] // 徽章
  dynamicCount?: number // 动态数量
  essenceCount?: number // 精华数量
  diggCount?: number // 获赞数量
  followCount?: number // 评论数量
  followed?: boolean // 评论数量
  authDesc?: string // 认证信息
  authIcon?: string // 认证图标
  authWidth?: number // 认证图标
  authHeight?: number // 认证图标
  onClickUser?(): void
  onClickMore?(): void
  onClickContent?(text: string): void
  onClickFollow?(curStatus: boolean): void
}

const UserDetail: React.FC<IProps> = ({
  avatar,
  userId,
  username,
  userVerified,
  verifyType,
  medals,
  dynamicCount,
  essenceCount,
  diggCount,
  followCount,
  followed,
  authDesc,
  authIcon,
  authWidth = 16,
  authHeight = 16,
  onClickUser,
  onClickMore,
  onClickContent,
  onClickFollow,
}) => {
  const [expanded, setExpanded] = useState(false)

  const { state } = useContext(GlobalStoreContext)

  const { update, curStatus } = useToggleFollow({
    userId,
    followed,
  })

  const handleFollow = useCallback(() => {
    update()
    if (onClickFollow) onClickFollow(curStatus)
  }, [update, onClickFollow, curStatus])

  return (
    <React.Fragment>
      <div className="tw-w-232 tw-p-24 tw-pt-20 tw-text-common-black tw-text-center">
        <Avatar
          avatar={avatar}
          userId={userId}
          username={username}
          verifyType={verifyType}
          userVerified={userVerified}
          size={84}
          verifySize={18}
          hoverTransition={true}
          onClick={onClickUser}
        />
        <p
          className="tw-leading-22 tw-mt-12 tw-text-16 tw-font-medium tw-truncate"
          onClick={onClickUser}
        >
          {ugcUserLink({ userId, username })(username)}
        </p>
        {authDesc ? (
          <p className="tw-text-12 tw-text-video-shallow-gray tw-mt-2">
            <span
              className={classNames({
                'tw-inline-block auth': authIcon,
              })}
            >
              {authDesc}
            </span>
          </p>
        ) : null}
        {medals?.length ? (
          <ul className="tw-text-0 tw-mt-4">
            {medals.slice(0, expanded ? undefined : 6).map((item, index) => {
              return (
                <ToolTip
                  key={index}
                  tag={'li'}
                  theme={'light'}
                  className="tw-mx-4 tw-mt-8 tw-inline-block"
                  content={<Medal {...item} />}
                >
                  <Image
                    width={20}
                    height={20}
                    className="tw-w-20 tw-h-20 tw-object-cover"
                    src={item.icon || ''}
                    alt={item.name}
                  />
                </ToolTip>
              )
            })}
          </ul>
        ) : null}
        {medals?.length && medals.length > 6 ? (
          <button
            type="button"
            className="tw-mt-6 tw-h-18 tw-text-12 tw-text-common-gray hover:tw-text-common-black"
            onClick={() => {
              setExpanded((prev) => !prev)
              if (onClickMore) onClickMore()
            }}
          >
            {expanded ? '收起徽章' : '更多徽章'}
            <i
              className={classNames(
                'DCD_Icon tw-text-14',
                expanded ? 'icon_triangle_up_12' : 'icon_triangle_down_12'
              )}
            />
          </button>
        ) : null}
        <div
          style={{ margin: '0 -20px' }}
          className={classNames(
            'tw-grid tw-grid-cols-4 tw-leading-20 tw-text-center tw-mx-auto tw-whitespace-nowrap',
            medals?.length && medals.length > 6 ? '' : 'tw-mt-4'
          )}
        >
          {ugcUserLink({ userId })(
            <div className="tw-pt-12" onClick={() => onClickContent?.('动态')}>
              <p className="tw-text-18 tw-font-medium font-W9V7O3XcYNRW_72Y font-ZKkmuNIJeX7dsXIq">
                {dynamicCount}
              </p>
              <p className="tw-mt-4 tw-text-14 tw-text-common-gray">动态</p>
            </div>
          )}
          {ugcUserLink({ userId })(
            <div className="tw-pt-12" onClick={() => onClickContent?.('精华')}>
              <p className="tw-text-18 tw-font-medium font-W9V7O3XcYNRW_72Y font-ZKkmuNIJeX7dsXIq">
                {essenceCount}
              </p>
              <p className="tw-mt-4 tw-text-14 tw-text-common-gray">精华</p>
            </div>
          )}
          {ugcUserLink({ userId, tab: 'gzyh' })(
            <div className="tw-pt-12" onClick={() => onClickContent?.('评论')}>
              <p className="tw-text-18 tw-font-medium font-W9V7O3XcYNRW_72Y font-ZKkmuNIJeX7dsXIq">
                {followCount}
              </p>
              <p className="tw-mt-4 tw-text-14 tw-text-common-gray">关注</p>
            </div>
          )}
          {ugcUserLink({ userId })(
            <div className="tw-pt-12" onClick={() => onClickContent?.('获赞')}>
              <p className="tw-text-18 tw-font-medium font-W9V7O3XcYNRW_72Y font-ZKkmuNIJeX7dsXIq">
                {diggCount}
              </p>
              <p className="tw-mt-4 tw-text-14 tw-text-common-gray">获赞</p>
            </div>
          )}
        </div>
        {userId && userId !== state.userData?.user_id ? (
          <button
            type="button"
            className={classNames(
              'tw-mt-12 tw-w-full tw-rounded-2 tw-h-32 tw-font-medium',
              {
                'tw-bg-common-yellow hover:tw-bg-common-highlight-yellow':
                  !curStatus,
                'tw-bg-video-gray': curStatus,
              }
            )}
            onClick={handleFollow}
          >
            {curStatus ? '已关注' : '关注TA'}
          </button>
        ) : null}
      </div>
      <style jsx>
        {`
          .tw-grid div:hover p {
            color: #e62021;
          }
        `}
      </style>
      <style jsx>
        {`
          .auth {
            padding-left: ${(authWidth * 16) / authHeight + 2}px;
            background: url(${authIcon}) left center /
              ${(authWidth * 16) / authHeight}px 16px no-repeat;
          }
        `}
      </style>
    </React.Fragment>
  )
}

export default UserDetail
